import { useEffect } from 'react'
import { useSearchParams } from 'react-router'
import { AlertTriangle, LayoutGrid, Lock, SearchX, Shield, ShieldOff, X, Grid3x3 } from 'lucide-react'
import { Link } from 'react-router'

import { useImageFeed } from '@/hooks/queries'
import { useAuth } from '@/hooks/use-auth'
import { useSafeMode } from '@/hooks/use-ui'
import { ImageGrid } from '@/components/gallery/image-grid'
import { Button } from '@/components/ui/button'
import { buttonClass } from '@/components/ui/button-variants'
import { EmptyState } from '@/components/ui/misc'
import { SkeletonGrid } from '@/components/gallery/skeleton-grid'
import { gallery, preferences, useStore } from '@/lib/store'
import { tagLabel } from '@/lib/tags'
import { cn, formatCount } from '@/lib/utils'

export default function GalleryPage() {
  const [params] = useSearchParams()
  const q = (params.get('q') ?? '').trim()
  const { safe } = useSafeMode()
  const feed = useImageFeed(q, !safe)
  const { images, hasNextPage, isFetchingNextPage, fetchNextPage } = feed

  // Let the viewer step through exactly what is on screen.
  useEffect(() => {
    gallery.set({
      ids: images.map((i) => i.id),
      hasMore: !!hasNextPage,
      loadMore: () => void fetchNextPage(),
    })
  }, [images, hasNextPage, fetchNextPage])

  useEffect(() => {
    document.title = q ? `${q} · Kagami` : 'Kagami · Hirax'
  }, [q])

  const terms = q ? q.split(/\s+/) : []

  return (
    <div className="mx-auto max-w-[110rem] px-2 sm:px-4">
      <Toolbar terms={terms} count={feed.count} loading={feed.isPending} />

      {feed.isPending ? (
        <SkeletonGrid />
      ) : feed.isError ? (
        <EmptyState icon={AlertTriangle} title="Couldn’t load images">
          <p>{(feed.error as Error).message}</p>
          <Button variant="outline" className="mt-5" onClick={() => void feed.refetch()}>
            Try again
          </Button>
        </EmptyState>
      ) : images.length === 0 ? (
        <EmptyState icon={SearchX} title="Nothing found">
          <p>
            No images match {terms.length ? 'all of these tags' : 'right now'}
            {safe ? ' in safe mode' : ''}. Try fewer tags or check the spelling.
          </p>
          {q && (
            <Link to="/" className={cn(buttonClass('outline'), 'mt-5')}>
              Clear search
            </Link>
          )}
        </EmptyState>
      ) : (
        <ImageGrid
          items={images}
          hasMore={!!hasNextPage}
          isFetchingMore={isFetchingNextPage}
          onLoadMore={() => void fetchNextPage()}
        />
      )}
    </div>
  )
}

function Toolbar({ terms, count, loading }: { terms: string[]; count: number; loading: boolean }) {
  const { safe, canToggle, toggle } = useSafeMode()
  const { login } = useAuth()
  const { density } = useStore(preferences)

  const removeTerm = (term: string) => {
    const rest = terms.filter((t) => t !== term).join(' ')
    return rest ? `/?q=${encodeURIComponent(rest)}` : '/'
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-1 pb-4 pt-2 sm:pb-5">
      <div className="min-w-0 flex-1">
        {terms.length === 0 ? (
          <h1 className="text-2xl font-semibold sm:text-3xl">
            Latest <span className="text-gradient">images</span>
          </h1>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5">
            <h1 className="sr-only">Search results</h1>
            {terms.map((term) => (
              <Link
                key={term}
                to={removeTerm(term)}
                className="inline-flex items-center gap-1 rounded-full border border-primary/25 bg-primary/10 py-1 pl-3 pr-2 text-sm font-medium text-primary hover:bg-primary/15"
                aria-label={`Remove ${tagLabel(term)}`}
              >
                {tagLabel(term)}
                <X className="size-3.5" />
              </Link>
            ))}
          </div>
        )}
        {terms.length > 0 && !loading && count > 0 && (
          <p className="mt-1.5 text-sm text-muted-foreground">{formatCount(count)} results</p>
        )}
      </div>

      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={canToggle ? toggle : login}
          aria-pressed={safe}
          title={canToggle ? (safe ? 'Showing safe images only' : 'Showing all ratings') : 'Sign in to change'}
          className={cn(
            'inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-sm font-medium transition-colors',
            safe
              ? 'border-success/30 bg-success/10 text-success'
              : 'border-destructive/30 bg-destructive/10 text-destructive',
          )}
        >
          {!canToggle ? <Lock className="size-4" /> : safe ? <Shield className="size-4" /> : <ShieldOff className="size-4" />}
          {safe ? 'Safe' : 'All ratings'}
        </button>

        <div className="hidden rounded-full border p-0.5 sm:flex" role="group" aria-label="Grid density">
          {(
            [
              ['comfortable', LayoutGrid, 'Large tiles'],
              ['compact', Grid3x3, 'Small tiles'],
            ] as const
          ).map(([value, Icon, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={density === value}
              title={label}
              onClick={() => preferences.set((p) => ({ ...p, density: value }))}
              className={cn(
                'rounded-full p-1.5 transition-colors',
                density === value ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="size-4" />
              <span className="sr-only">{label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
